import { create } from 'zustand'
import { persist } from 'zustand/middleware'

interface User {
  id: string
  email: string
  first_name: string
  last_name: string
  full_name: string
  is_platform_staff: boolean
  is_superuser?: boolean
  sales_partner?: {
    id: string
    full_name: string
    role: string
    role_display: string
    county: string
    is_active: boolean
  } | null
}

interface Business {
  id: string
  name: string
  slug: string
  logo: string | null
  primary_color: string | null
  secondary_color: string | null
  is_owner: boolean
  portal_only: boolean
}

export interface ActiveBranch {
  id: string
  name: string
  branch_type: string
}

interface AuthState {
  user: User | null
  accessToken: string | null
  refreshToken: string | null
  sessionId: string | null
  activeBusiness: Business | null
  activeBranch: ActiveBranch | null
  setAuth: (user: User, access: string, refresh: string, sessionId?: string) => void
  setTokens: (access: string, refresh: string) => void
  setSessionId: (id: string) => void
  setActiveBusiness: (business: Business) => void
  setActiveBranch: (branch: ActiveBranch | null) => void
  clearAuth: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      sessionId: null,
      activeBusiness: null,
      activeBranch: null,

      setAuth: (user, access, refresh, sessionId) =>
        set({ user, accessToken: access, refreshToken: refresh, sessionId: sessionId ?? null }),

      setTokens: (access, refresh) =>
        set({ accessToken: access, refreshToken: refresh }),

      setSessionId: (id) => set({ sessionId: id }),

      setActiveBusiness: (business) =>
        set((state) => ({
          activeBusiness: business,
          // Only clear the active branch when actually switching to a
          // different business. AppLayout re-fetches /tenancy/me/ on every
          // mount (including when staff switch from the Employee Portal
          // into the ERP), and without this guard that re-fetch would wipe
          // out the branch that was correctly set at login.
          activeBranch: state.activeBusiness?.id === business.id ? state.activeBranch : null,
        })),

      setActiveBranch: (branch) => set({ activeBranch: branch }),

      clearAuth: () =>
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          sessionId: null,
          activeBusiness: null,
          activeBranch: null,
        }),
    }),
    { name: 'distrihub-auth' },
  ),
)
